import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import style from "./index.module.scss"
import WrapperContent from "../../shared/WrapperContent";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import CloseIcon from "../../shared/icons/CloseIcon";
import InputCustom from "../../shared/forms/input-custom";
import UserIcon from "../../shared/icons/UserIcon";
import PhoneIcon from "../../shared/icons/PhoneIcon";
import EmailIcon from "../../shared/icons/EmailIcon";
import { Employee, User } from '../../../services';
import { requestErrorDisplay } from '../../../utils';
import InputMask from 'react-input-mask';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import Loading from '../../shared/Loading/Loading';
import cn from 'classnames';
import SelectCustom from '../../shared/forms/select-custom';
import Form from 'react-bootstrap/Form';
import ClientRoles from '../../../services/ClientRoles';

const PHONE_MASK = '+79999999999';

const WorkersEditPage = () => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state);
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const driversUrl = "/workers";
  const backUrl = searchParams.get("redirectUrl") ? searchParams.get("redirectUrl") : driversUrl;
  const [isLoading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [surname, setSurName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [roleData, setRoleData] = useState([]);
  const [roleId, setRoleId] = useState("");

  const STATUS = [
    { title: t('workersEdit.status.active'), value: 'Confirmed' },
    { title: t('workersEdit.status.inactive'), value: 'UnActive' },
  ];

  useEffect(() => {
    if (id) {
      loadEmployeeData(id);
    }
  }, [id]);

  useEffect(() => {
    getRoles();
  }, []);

  const getRoles = () => {
    ClientRoles.getAll()
        .then(response => {
          const data = [{id: '', name: '-'}];
          response.data.forEach((role) => {
            data.push(role);
          });
          setRoleData(data);
        })
        .catch(error => {
          console.log('error', error);
        });
  };

  const handleSaveRole = () => {
    if (!roleId) return;

    User.updateRole(id, roleId)
        .then(response => {
          toast.success(t('workersEdit.toast.roleUpdatedSuccess'));
        })
        .catch(error => {
          toast.error(t('workersEdit.toast.roleUpdateError'));
          console.log(error);
        });
  };

  const loadEmployeeData = async (employeeId) => {
    try {
      const response = await Employee.getEmployee(employeeId);
      setName(response.data.name);
      setSurName(response.data.surname);
      setEmail(response.data.email);
      setPhoneNumber(response.data.phoneNumber);
      setStatus(response.data.state);

      setRoleId(response.data.role || "");

      const selectedStatusObject = STATUS.find(status => status.value.toLowerCase() === response.data.state.toLowerCase());
      setSelectedStatus(selectedStatusObject);
    } catch (error) {
      console.error(t('workersEdit.errors.loadDataError'), error);
    }
  };

  const validateFormatData = () => {
    if (!name) {
      toast.error(t('workersEdit.validation.nameRequired'));
      return false;
    }

    if (!surname) {
      toast.error(t('workersEdit.validation.surnameRequired'));
      return false;
    }

    if (!email) {
      toast.error(t('workersEdit.validation.emailRequired'));
      return false;
    }

    return true;
  }

  const onSubmit = async () => {
    if (!validateFormatData()) {
      return;
    }

    if (roleId) {
      handleSaveRole();
    }

    const updatedData = {
      name: name,
      surname: surname,
      patronymic: "",
      iin: "",
      phoneNumber: phoneNumber,
      state: status,
      role: roleId
    };

    setLoading(true);

    try {
      const res = await Employee.editEmployee(id, updatedData);
      if (res.status === 200) {
        toast.success(t('workersEdit.toast.dataUpdatedSuccess'));
        navigate(backUrl);
        return res.data;
      } else {
        toast.error(t('workersEdit.toast.dataUpdateError'));
      }
    } catch (error) {
      requestErrorDisplay(error, t);
    } finally {
      setLoading(false);
    }
  };

  return (
      <WrapperContent>
        <div className={style.wrapper}>
          <h1 className={style.title}>{t('workersEdit.title')}</h1>
          <div className={style.content}>
            <div className={style.field}>
              <span className={style.field_label}>{t('workersEdit.label.name')}</span>
              <InputCustom
                  placeholder={t('workersEdit.placeholder.enterName')}
                  defaultValue={name}
                  onChangeValue={setName}
                  isDisabled={true}
                  prefixStart={<UserIcon />}
              />
            </div>
            <div className={style.field}>
              <span className={style.field_label}>{t('workersEdit.label.surname')}</span>
              <InputCustom
                  placeholder={t('workersEdit.placeholder.enterSurname')}
                  defaultValue={surname}
                  onChangeValue={setSurName}
                  isDisabled={true}
                  prefixStart={<UserIcon />}
              />
            </div>
            {/* Новое поле для выбора роли */}
            <div className={style.field}>
              <span className={style.field_label}>{t('workersEdit.label.role')}</span>
              <Form.Group className="mb-3 w-100">
                {
                  roleData && roleData.length > 0 ?
                      <Form.Control
                          as="select"
                          value={roleId}
                          onChange={e => setRoleId(e.target.value)}
                          className={style.select_control}>
                        {roleData.map(item => {
                          return (
                              <option key={item.id} value={item.id}>{item.name}</option>
                          );
                        })}
                      </Form.Control> :
                      <Loading inBlock marginBottom={0} />
                }
              </Form.Group>
            </div>
            <div className={style.field}>
              <span className={style.field_label}>Email</span>
              <InputCustom
                  defaultValue={email}
                  onChangeValue={setEmail}
                  placeholder="example@mail.com"
                  isDisabled={true}
                  prefixStart={<EmailIcon />}
              />
            </div>
            <div className={style.field}>
            <span className={style.field_label}>{t('workersEdit.label.phoneNumber')}
              <span className={style.optional}>{t('driversCreate.label.optional')}</span>
            </span>
              <InputMask
                  value={phoneNumber}
                  maskChar={null}
                  mask={PHONE_MASK}
                  onChange={(event) => setPhoneNumber(event.target.value)}
                  disabled={false}
              >
                {(inputProps) => (
                    <InputCustom
                        {...inputProps}
                        placeholder={PHONE_MASK}
                        prefixStart={<PhoneIcon />}
                        defaultValue={phoneNumber}
                        onChangeValue={setPhoneNumber}
                    />
                )}
              </InputMask>
            </div>
            <div className={style.field}>
              <span className={style.field_label}>{t('workersEdit.label.status')}</span>
              <SelectCustom
                  key={'status-select'}
                  placeholder={t('workersEdit.placeholder.status')}
                  keyId="value"
                  keyName="title"
                  isDisabled={status === 'Draft'}
                  options={STATUS}
                  defaultValue={selectedStatus}
                  returnObject
                  onSubmit={(item) => {
                    console.log("item", item);
                    setStatus(item.value);
                    setSelectedStatus(item);
                  }}
              />
            </div>

            <Link to={backUrl} className={style.buttonClose}>
              <CloseIcon />
            </Link>
          </div>
          <div className={style.block}>
            <Link to={backUrl} className={style.buttonCancel}>
              {t('workersEdit.button.cancel')}
            </Link>

            <button
                className={cn(style.button_upload, { [style.button_upload_loading]: isLoading })}
                onClick={() => onSubmit()}
            >
              {!isLoading && <span>{t('common.buttons.save')}</span>}
              {isLoading && <Loading inBlock marginBottom={0} />}
            </button>
          </div>
        </div>
      </WrapperContent>
  );
};

export default WorkersEditPage;
